import React, { useState, useEffect } from "react";
import { useReferralListSlice } from "../slice";
import { formatPhoneNumber } from "utils/format";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "app/components/ui/collapsible";
import ToothSelector from "./ToothSelected";

interface ReferralData {
  patientFirstName: string;
  patientLastName: string;
  patientPhone: string;
  patientEmail: string;
  referringDoctorName: string;
  practiceName: string;
  doctorPhone: string;
  doctorEmail: string;
  remarks: string;
  selectedTeeth?: number[];
}

interface Props {
  isFormOpen: boolean;
  isNotesOpen: boolean;
  setIsFormOpen: (isFormOpen: boolean) => void;
  referralId: string;
}

export default function ReferralDetailPanel({ isFormOpen, isNotesOpen, setIsFormOpen, referralId }: Props) {
  const { useGetReferralByIdQuery } = useReferralListSlice();
  const { data: referral } = useGetReferralByIdQuery({ referralId: referralId }, { skip: !referralId });
  const [referralData, setReferralData] = useState<ReferralData | null>(null);
  const referData = referral?.data;

  useEffect(() => {
    if (referData && referralId) {
      setReferralData(referData);
    } else {
      setReferralData(null);
    }
  }, [referral, referralId]);

  return (
    <Collapsible
      open={isFormOpen}
      onOpenChange={setIsFormOpen}
      className="overflow-hidden bg-white rounded-lg"
    >
      <div className="flex items-center justify-between bg-[#DDE1E6] p-4 rounded-t-lg">
        <h2 className="font-serif text-[#21272A] text-base font-semibold leading-[1.2rem]">
          Provider Referral Form
        </h2>
        <CollapsibleTrigger asChild>
          <button
            className="inline-flex items-center justify-center bg-white hover:bg-[#FAFAFA] size-5 rounded"
            tabIndex={0}
            aria-label={isFormOpen ? "Collapse" : "Expand"}
          >
            <img
              src="/images/chevron.svg" 
              alt="" 
              className={`transition-transform duration-200 ${isFormOpen ? '':'rotate-180'}`}
            />
          </button>
        </CollapsibleTrigger>
      </div>
      <CollapsibleContent
        className={`custom-scrollbar overflow-y-auto max-h-[calc(100vh-21.5rem)] ${
          isFormOpen && isNotesOpen
            ? 'h-[calc(100vh-33.575rem)]'
            : isFormOpen
            ? 'h-[calc(100vh-22rem)]'
            : ''
        }`}
      >
        {!referralData || !referralId ? (   
          <div className="flex items-center justify-center h-full text-[#697077] text-sm font-normal leading-[1.225rem]">
            No referral selected.
          </div>
        ) : (
          <>
            {/* Patient's Details */}
            <div className="px-4 py-3 border-b border-[#DDE1E6]">
              <h4 className="text-[#5B73F1] text-xs font-normal leading-[0.9rem] mb-3">
                Patient's Details
              </h4>
              <div className="grid grid-cols-2 gap-y-5">
                <div className="space-y-0.5">
                  <h6 className="text-[#697077] text-xs font-light leading-[0.9rem]">
                    First Name
                  </h6>
                  <p className="text-[#21272A] text-sm font-normal leading-[1.05rem]">
                    {referralData?.patientFirstName || '-'}
                  </p>
                </div>
                <div className="space-y-0.5">
                  <h6 className="text-[#697077] text-xs font-light leading-[0.9rem]">
                    Last Name
                  </h6>
                  <p className="text-[#21272A] text-sm font-normal leading-[1.05rem]">
                    {referralData?.patientLastName || '-'}
                  </p>
                </div>
                <div className="space-y-0.5">
                  <h6 className="text-[#697077] text-xs font-light leading-[0.9rem]">
                    Phone
                  </h6>
                  <p className="text-[#21272A] text-sm font-normal leading-[1.05rem]">
                    {formatPhoneNumber(referralData?.patientPhone) || '-'}
                  </p>
                </div>
                <div className="space-y-0.5">
                  <h6 className="text-[#697077] text-xs font-light leading-[0.9rem]">
                    Email
                  </h6>
                  <p className="text-[#21272A] text-sm font-normal leading-[1.05rem]">
                    {referralData?.patientEmail || '-'}
                  </p>
                </div>
              </div>
            </div>
            {/* Doctor's Details */}
            <div className="px-4 py-3 border-b border-[#DDE1E6]">
              <h4 className="text-[#5B73F1] text-xs font-normal leading-[0.9rem] mb-3">
                Doctor's Details
              </h4>
              <div className="grid grid-cols-2 gap-y-5">
                <div className="space-y-0.5">
                  <h6 className="text-[#697077] text-xs font-light leading-[0.9rem]">
                    Referring Doctor
                  </h6>
                  <p className="text-[#21272A] text-sm font-normal leading-[1.05rem]">
                    {referralData?.referringDoctorName || '-'}
                  </p>
                </div>
                <div className="space-y-0.5">
                  <h6 className="text-[#697077] text-xs font-light leading-[0.9rem]">
                    Practice Name
                  </h6>
                  <p className="text-[#21272A] text-sm font-normal leading-[1.05rem]">
                    {referralData?.practiceName || '-'}
                  </p>
                </div>
                <div className="space-y-0.5">
                  <h6 className="text-[#697077] text-xs font-light leading-[0.9rem]">
                    Phone
                  </h6>
                  <p className="text-[#21272A] text-sm font-normal leading-[1.05rem]">
                    {formatPhoneNumber(referralData?.doctorPhone) || '-'}
                  </p>
                </div>
                <div className="space-y-0.5">
                  <h6 className="text-[#697077] text-xs font-light leading-[0.9rem]">
                    Email
                  </h6>
                  <p className="text-[#21272A] text-sm font-normal leading-[1.05rem]">
                    {referralData?.doctorEmail || '-'}
                  </p>
                </div>
              </div>
            </div>
            {/* Dental Image */}
            <div className="px-4 py-3 border-b border-[#DDE1E6]">
              <ToothSelector teethNumbers={referralData?.selectedTeeth || []} />
              {/* <img src="/images/dental-image.png" alt="Dental Image" className="object-cover w-full h-full" /> */}
            </div>
            {/* Remarks */}
            <div className="px-4 py-3 space-y-0.5">
              <h6 className="text-[#697077] text-xs font-light leading-[0.9rem]">
                Remarks
              </h6>
              <p className="text-[#21272A] text-sm font-normal leading-[1.3125rem] whitespace-pre-line">
                {referralData?.remarks || '-'}
              </p>
            </div>
          </>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
